import { useTranslation } from "react-i18next"

import type { GoNoGo, ReviewPriorityItem, ReviewPriorityItemImpact } from "../../types"

import { AlertTriangle, CheckCircle2, XCircle } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"

import { scoreColor } from "./bid-review-constants"
import { CitationText, type ExtractionItemLookup, type OpenSourceFn } from "./extraction-citation"

// ── Types ─────────────────────────────────────────────────────────────────────

type SummaryTabProps = {
  goNoGo?: GoNoGo
  goNoGoReasoning?: string
  totalPredictedScore?: number
  totalMaxScore?: number
  overallAssessment?: string
  priorityItems?: ReviewPriorityItem[]
  improvementOpportunities?: ReviewPriorityItem[]
  strengthsSummary?: string[]
  competitiveNotes?: string
  extractionLookup?: ExtractionItemLookup
  onOpenSource?: OpenSourceFn
  isLoading: boolean
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const GO_NO_GO_STYLE: Record<
  GoNoGo,
  { icon: React.ElementType; iconClass: string; containerClass: string }
> = {
  go: {
    icon: CheckCircle2,
    iconClass: "text-success",
    containerClass: "border-success bg-success/15",
  },
  cautious_go: {
    icon: AlertTriangle,
    iconClass: "text-warning",
    containerClass: "border-warning bg-warning/10",
  },
  no_go: {
    icon: XCircle,
    iconClass: "text-destructive",
    containerClass: "border-destructive/30 bg-destructive/10",
  },
}

const IMPACT_STYLE: Record<ReviewPriorityItemImpact, { badgeClass: string }> = {
  critical: {
    badgeClass: "border-destructive bg-destructive/10 text-destructive",
  },
  high: {
    badgeClass: "border-primary-ink bg-primary/10 text-primary-ink",
  },
  medium: {
    badgeClass: "border-warning bg-warning/10 text-warning-foreground",
  },
  low: {
    badgeClass: "border-accent bg-accent/50 text-accent-foreground",
  },
}

// ── Priority item row ─────────────────────────────────────────────────────────

function PriorityItemRow({
  item,
  extractionLookup,
  onOpenSource,
}: {
  item: ReviewPriorityItem
  extractionLookup?: ExtractionItemLookup
  onOpenSource?: OpenSourceFn
}) {
  const { t } = useTranslation()
  const impactConf = IMPACT_STYLE[item.impact]
  const impactLabel = t(`tender.bidReview.summaryTab.impact.${item.impact}`)

  return (
    <div className="bg-muted/20 flex items-start gap-3 rounded-lg border px-3 py-2.5">
      <span className="text-muted-foreground mt-0.5 min-w-[20px] shrink-0 text-[12px] font-bold tabular-nums">
        {item.rank}.
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[13px] font-medium">
            {extractionLookup ? (
              <CitationText
                lookup={extractionLookup}
                text={item.title}
                onOpenSource={onOpenSource}
              />
            ) : (
              item.title
            )}
          </span>
          <Badge className={cn("h-4 px-1.5 text-[10px]", impactConf.badgeClass)} variant="outline">
            {impactLabel}
          </Badge>
        </div>
        {extractionLookup ? (
          <CitationText
            as="p"
            className="text-muted-foreground mt-0.5 text-[12px] leading-relaxed"
            lookup={extractionLookup}
            text={item.description}
            onOpenSource={onOpenSource}
          />
        ) : (
          <p className="text-muted-foreground mt-0.5 text-[12px] leading-relaxed">
            {item.description}
          </p>
        )}
      </div>
    </div>
  )
}

// ── Loading skeleton ──────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-16 w-full" />
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-4/5" />
      </div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function SummaryTab({
  goNoGo,
  goNoGoReasoning,
  totalPredictedScore,
  totalMaxScore,
  overallAssessment,
  priorityItems,
  improvementOpportunities,
  strengthsSummary,
  competitiveNotes,
  extractionLookup,
  onOpenSource,
  isLoading,
}: SummaryTabProps) {
  const { t } = useTranslation()
  if (isLoading) {
    return <LoadingSkeleton />
  }

  const hasPriorities = (priorityItems?.length ?? 0) > 0
  const hasImprovements = (improvementOpportunities?.length ?? 0) > 0
  const hasStrengths = (strengthsSummary?.length ?? 0) > 0

  return (
    <div className="flex flex-col gap-5">
      {goNoGo && (
        <div className={cn("rounded-lg border p-4", GO_NO_GO_STYLE[goNoGo].containerClass)}>
          <div className="flex items-center gap-3">
            {(() => {
              const conf = GO_NO_GO_STYLE[goNoGo]
              const GoNoGoIcon = conf.icon
              return (
                <>
                  <GoNoGoIcon className={cn("size-6 shrink-0", conf.iconClass)} />
                  <span className="text-base font-semibold">
                    {t(`tender.bidReview.summaryTab.goNoGo.${goNoGo}`)}
                  </span>
                </>
              )
            })()}
          </div>
          {goNoGoReasoning &&
            (extractionLookup ? (
              <CitationText
                as="p"
                className="mt-2 text-[13px] leading-relaxed"
                lookup={extractionLookup}
                text={goNoGoReasoning}
                onOpenSource={onOpenSource}
              />
            ) : (
              <p className="mt-2 text-[13px] leading-relaxed">{goNoGoReasoning}</p>
            ))}
        </div>
      )}

      {totalPredictedScore != null && totalMaxScore != null && (
        <div className="flex items-center gap-4">
          <div className="flex flex-col items-center gap-0.5">
            <span
              className={cn(
                "text-3xl leading-none font-bold tabular-nums",
                scoreColor(totalPredictedScore, totalMaxScore)
              )}
            >
              {totalPredictedScore}
            </span>
            <span className="text-muted-foreground/60 text-[10px] font-medium tracking-wide whitespace-nowrap uppercase">
              {t("tender.bidReview.summaryTab.scoreDenom", { max: totalMaxScore })}
            </span>
          </div>
          {overallAssessment &&
            (extractionLookup ? (
              <CitationText
                as="p"
                className="text-[13px] leading-relaxed"
                lookup={extractionLookup}
                text={overallAssessment}
                onOpenSource={onOpenSource}
              />
            ) : (
              <p className="text-[13px] leading-relaxed">{overallAssessment}</p>
            ))}
        </div>
      )}

      {(totalPredictedScore == null || totalMaxScore == null) && overallAssessment && (
        <div className="bg-muted/30 rounded-lg border p-4">
          {extractionLookup ? (
            <CitationText
              as="p"
              className="text-[13px] leading-relaxed"
              lookup={extractionLookup}
              text={overallAssessment}
              onOpenSource={onOpenSource}
            />
          ) : (
            <p className="text-[13px] leading-relaxed">{overallAssessment}</p>
          )}
        </div>
      )}

      {hasPriorities && (
        <section>
          <h4 className="mb-2 text-[12px] font-semibold tracking-wide uppercase">
            {t("tender.bidReview.summaryTab.mustFix")}
          </h4>
          <div className="flex flex-col gap-2">
            {priorityItems!.map((item) => (
              <PriorityItemRow
                key={item.rank}
                extractionLookup={extractionLookup}
                item={item}
                onOpenSource={onOpenSource}
              />
            ))}
          </div>
        </section>
      )}

      {hasImprovements && (
        <section>
          <h4 className="mb-2 text-[12px] font-semibold tracking-wide uppercase">
            {t("tender.bidReview.summaryTab.improvements")}
          </h4>
          <div className="flex flex-col gap-2">
            {improvementOpportunities!.map((item) => (
              <PriorityItemRow
                key={item.rank}
                extractionLookup={extractionLookup}
                item={item}
                onOpenSource={onOpenSource}
              />
            ))}
          </div>
        </section>
      )}

      {hasStrengths && (
        <section>
          <h4 className="mb-2 text-[12px] font-semibold tracking-wide uppercase">
            {t("tender.bidReview.summaryTab.strengths")}
          </h4>
          <ul className="flex flex-col gap-1.5">
            {strengthsSummary!.map((strength, i) => (
              <li key={i} className="flex items-start gap-2 text-[13px]">
                <CheckCircle2 className="text-success mt-0.5 size-3.5 shrink-0" />
                {extractionLookup ? (
                  <CitationText
                    lookup={extractionLookup}
                    text={strength}
                    onOpenSource={onOpenSource}
                  />
                ) : (
                  strength
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {competitiveNotes &&
        (extractionLookup ? (
          <CitationText
            as="p"
            className="text-muted-foreground text-[12px] leading-relaxed"
            lookup={extractionLookup}
            text={competitiveNotes}
            onOpenSource={onOpenSource}
          />
        ) : (
          <p className="text-muted-foreground text-[12px] leading-relaxed">{competitiveNotes}</p>
        ))}
    </div>
  )
}
